import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { rate } from '../lib/money';
import { stageOf } from '../lib/ticketStage';
import { useConfirm } from '../components/ConfirmDialog';
import ContractorContracts from '../components/contracts/ContractorContracts';
import { useFeature } from '../lib/features';
import '../components/TicketStageTabs.css';
import './ContractorProfile.css';

const ContractorProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contractor, setContractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [availability, setAvailability] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);

  // These four have had working, tested endpoints all along - nothing in the
  // app was calling them.
  const [skills, setSkills] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [billing, setBilling] = useState([]);
  const [workload, setWorkload] = useState(null);
  const [tabError, setTabError] = useState(null);
  const [invoiceQuery, setInvoiceQuery] = useState('');
  const [invoiceResult, setInvoiceResult] = useState(null);

  const [newSkill, setNewSkill] = useState({ skill_name: '', proficiency_level: 'intermediate', years_experience: 0 });
  const [newCert, setNewCert] = useState({ certification_name: '', issuing_organization: '', expiry_date: '' });
  const [confirm, confirmDialog] = useConfirm();
  // The Contract tab (120), while the Contracts switch is on.
  const contractsOn = useFeature('contracts');
  // en-GB, Malta time: a US-locale machine would otherwise show 10/26/2026.
  const dateOf = (v) => new Date(v).toLocaleDateString('en-GB', { timeZone: 'Europe/Malta' });

  const PROFICIENCY = ['beginner', 'intermediate', 'advanced', 'expert'];
  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const asArray = (data, key) =>
    Array.isArray(data) ? data : (data && Array.isArray(data[key]) ? data[key] : []);

  const loadSkills = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/contractors/${id}/skills`);
      setSkills(asArray(res.data, 'skills'));
    } catch (err) { console.error('Could not load skills:', err); }
  }, [API_URL, id]);

  const loadCertifications = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/contractors/${id}/certifications`);
      setCertifications(asArray(res.data, 'certifications'));
    } catch (err) { console.error('Could not load certifications:', err); }
  }, [API_URL, id]);

  const loadBilling = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/contractors/${id}/billing?limit=25`);
      setBilling(asArray(res.data, 'history'));
    } catch (err) { console.error('Could not load billing:', err); }
  }, [API_URL, id]);

  const loadWorkload = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/contractors/${id}/workload`);
      setWorkload(res.data || null);
    } catch (err) { console.error('Could not load workload:', err); }
  }, [API_URL, id]);

  const loadAvailability = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/contractors/${id}/availability`);
      setAvailability(asArray(res.data, 'availability_blocks'));
    } catch (err) { console.error('Could not load availability:', err); }
  }, [API_URL, id]);

  // Load a tab's data the first time it is opened rather than all at once.
  useEffect(() => {
    if (!id) return;
    if (activeTab === 'skills') loadSkills();
    if (activeTab === 'certifications') loadCertifications();
    if (activeTab === 'billing') loadBilling();
    if (activeTab === 'workload') loadWorkload();
  }, [activeTab, id, loadSkills, loadCertifications, loadBilling, loadWorkload]);

  // What did a given invoice actually cover? This is the check NCMB does when
  // a contractor's invoice arrives.
  const lookupInvoice = async (e) => {
    e.preventDefault();
    setTabError(null); setInvoiceResult(null);
    const ref = invoiceQuery.trim();
    if (!ref) return;
    try {
      const res = await axios.get(`${API_URL}/billing/invoices/${encodeURIComponent(ref)}`);
      setInvoiceResult(res.data);
    } catch (err) {
      setTabError(err.response?.status === 404
        ? `Nothing has been billed against ${ref}`
        : 'Could not look up that invoice');
    }
  };

  const handleAddSkill = async (e) => {
    e.preventDefault();
    setTabError(null);
    try {
      await axios.post(`${API_URL}/contractors/${id}/skills`, newSkill);
      setNewSkill({ skill_name: '', proficiency_level: 'intermediate', years_experience: 0 });
      await loadSkills();
    } catch (err) {
      setTabError(err.response?.data?.error || 'Could not add that skill');
    }
  };

  const handleDeleteSkill = async (skill) => {
    if (!(await confirm({ title: `Remove the skill "${skill.skill_name}"?`,
      message: skill.is_verified ? 'It was verified; that goes with it.' : null, confirmLabel: 'Remove', danger: true }))) return;
    setTabError(null);
    try {
      await axios.delete(`${API_URL}/contractors/${id}/skills/${skill.id}`);
      await loadSkills();
    } catch (err) {
      setTabError(err.response?.data?.error || 'Could not remove that skill');
    }
  };

  const handleVerifySkill = async (skillId) => {
    setTabError(null);
    try {
      await axios.put(`${API_URL}/contractors/${id}/skills/${skillId}/verify`);
      await loadSkills();
    } catch (err) {
      setTabError(err.response?.data?.error || 'Could not verify that skill');
    }
  };

  const handleAddCertification = async (e) => {
    e.preventDefault();
    setTabError(null);
    try {
      await axios.post(`${API_URL}/contractors/${id}/certifications`, newCert);
      setNewCert({ certification_name: '', issuing_organization: '', expiry_date: '' });
      await loadCertifications();
    } catch (err) {
      setTabError(err.response?.data?.error || 'Could not add that certification');
    }
  };

  const handleDeleteCertification = async (cert) => {
    if (!(await confirm({ title: `Remove the certification "${cert.certification_name}"?`,
      confirmLabel: 'Remove', danger: true }))) return;
    setTabError(null);
    try {
      await axios.delete(`${API_URL}/contractors/${id}/certifications/${cert.id}`);
      await loadCertifications();
    } catch (err) {
      setTabError(err.response?.data?.error || 'Could not remove that certification');
    }
  };

  const handleSetMaxTickets = async (value) => {
    setTabError(null);
    try {
      await axios.put(`${API_URL}/contractors/${id}/workload/max`, { max_concurrent_tickets: value });
      await loadWorkload();
    } catch (err) {
      setTabError(err.response?.data?.error || 'Could not update capacity');
    }
  };

  const handleToggleDay = async (dayIndex, available) => {
    setTabError(null);
    try {
      // /availability/weekly expects { schedule: [...] } and only ever
      // inserts, so re-saving a day collided with the unique index. The
      // pattern endpoint replaces the whole week in one transaction.
      const next = DAY_NAMES.map((_, i) => {
        const existing = availability.filter((b) => b.day_of_week === i && b.is_available !== false);
        if (i === dayIndex) {
          return available ? { day_of_week: i, start_time: '09:00', end_time: '17:00', is_available: true } : null;
        }
        return existing.length > 0
          ? {
              day_of_week: i,
              start_time: String(existing[0].start_time).slice(0, 5),
              end_time: String(existing[0].end_time).slice(0, 5),
              is_available: true
            }
          : null;
      }).filter(Boolean);
      await axios.put(`${API_URL}/contractors/${id}/availability/pattern`, { schedule: next });
      await loadAvailability();
    } catch (err) {
      setTabError(err.response?.data?.error || 'Could not update availability');
    }
  };

  // Fetch contractor profile
  useEffect(() => {
    const fetchContractor = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/contractors/${id}`);
        setContractor(response.data);
        setEditData(response.data);
        
        // Fetch availability
        try {
          const availRes = await axios.get(`${API_URL}/contractors/${id}/availability`);
          setAvailability(availRes.data.availability_blocks || []);
        } catch {
          // Optional panel: the profile reads without it.
        }

        // Fetch recent tickets
        try {
          const ticketsRes = await axios.get(`${API_URL}/contractors/${id}/tickets?limit=5`);
          const t = ticketsRes.data;
          setRecentTickets(Array.isArray(t) ? t : (t?.tickets ?? []));
        } catch {
          // Optional panel: the profile reads without it.
        }

        // Fetch performance metrics
        try {
          const metricsRes = await axios.get(`${API_URL}/contractors/${id}/performance-metrics`);
          setPerformanceMetrics(metricsRes.data);
        } catch {
          // Optional panel: the profile reads without it.
        }

        setError(null);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load contractor profile');
        console.error('Error fetching contractor:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchContractor();
    }
  }, [id, API_URL]);

  // Handle refresh
  const handleRefresh = () => {
    setLoading(true);
    const fetchContractor = async () => {
      try {
        const response = await axios.get(`${API_URL}/contractors/${id}`);
        setContractor(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to refresh contractor profile');
      } finally {
        setLoading(false);
      }
    };
    fetchContractor();
  };

  // Handle edit changes
  const handleEditChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle save
  const handleSave = async () => {
    try {
      const response = await axios.put(`${API_URL}/contractors/${id}`, editData);
      setContractor(response.data);
      setIsEditing(false);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save contractor');
      console.error('Error saving contractor:', err);
    }
  };

  // Handle cancel edit
  const handleCancel = () => {
    setEditData(contractor);
    setIsEditing(false);
  };

  if (loading) {
    return <div className="contractor-profile loading">Loading contractor profile...</div>;
  }

  if (error) {
    return (
      <div className="contractor-profile error">
        <div>
          <p>{error}</p>
          <button onClick={() => navigate(-1)} className="btn-secondary">Go Back</button>
        </div>
      </div>
    );
  }

  if (!contractor) {
    return (
      <div className="contractor-profile error">
        <div>
          <p>Contractor not found</p>
          <button onClick={() => navigate(-1)} className="btn-secondary">Go Back</button>
        </div>
      </div>
    );
  }

  // Calculate tier based on rating
  const getTier = (rating) => {
    if (!rating) return 'standard';
    if (rating >= 4.7) return 'elite';
    if (rating >= 4.0) return 'premium';
    return 'standard';
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get availability display
  const tier = getTier(contractor.average_rating);

  return (
    <div className="contractor-profile">
      {/* Header */}
      <div className="profile-header">
        <div className="header-info">
          <h1>{contractor.name}</h1>
          <p className="email">{contractor.email}</p>
          <div className="status-badges">
            <span className={`badge tier-${tier || 'standard'}`}>{(tier || 'standard').toUpperCase()}</span>
            <span className={`badge ${contractor.availability_status === 'available' ? 'available' : 'unavailable'}`}>
              {contractor.availability_status?.toUpperCase() || 'AVAILABLE'}
            </span>
            {contractor.is_certified && (
              <span className="badge certified">CERTIFIED</span>
            )}
            {contractor.average_rating && (
              <span className="badge performance">
                Rating: {contractor.average_rating.toFixed(1)}/5.0
              </span>
            )}
          </div>
        </div>
        <button className="refresh-btn" onClick={handleRefresh}>Refresh</button>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          {[
            ['overview', 'Overview'],
            ['performance', 'Performance'],
            ['skills', 'Skills'],
            ['certifications', 'Certifications'],
            ['availability', 'Availability'],
            ['workload', 'Workload'],
            ['billing', 'Billing'],
            ...(contractsOn ? [['contracts', 'Contracts']] : []),
            ['tickets', 'Recent tickets']
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`tab-button ${activeTab === key ? 'active' : ''}`}
              onClick={() => { setActiveTab(key); setTabError(null); }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="overview-section">
            {isEditing ? (
              <div className="edit-form">
                <h2>Edit Contractor Information</h2>
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={editData.name || ''}
                    onChange={(e) => handleEditChange('name', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editData.email || ''}
                    onChange={(e) => handleEditChange('email', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={editData.phone || ''}
                    onChange={(e) => handleEditChange('phone', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Company</label>
                  <input
                    type="text"
                    value={editData.company || ''}
                    onChange={(e) => handleEditChange('company', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Hourly Rate (€)</label>
                  <input
                    type="number"
                    value={editData.hourly_rate || ''}
                    onChange={(e) => handleEditChange('hourly_rate', parseFloat(e.target.value))}
                  />
                </div>
                <div className="form-group">
                  <label>Billing Rate (€)</label>
                  <input
                    type="number"
                    value={editData.billing_rate || ''}
                    onChange={(e) => handleEditChange('billing_rate', parseFloat(e.target.value))}
                  />
                </div>
                <div className="form-group">
                  <label>Max Concurrent Tickets</label>
                  <input
                    type="number"
                    value={editData.max_concurrent_tickets || ''}
                    onChange={(e) => handleEditChange('max_concurrent_tickets', parseInt(e.target.value))}
                  />
                </div>
                <div className="form-group">
                  <label>Availability Status</label>
                  <select
                    value={editData.availability_status || ''}
                    onChange={(e) => handleEditChange('availability_status', e.target.value)}
                  >
                    <option value="available">Available</option>
                    <option value="unavailable">Unavailable</option>
                    <option value="on-leave">On Leave</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={editData.is_certified || false}
                      onChange={(e) => handleEditChange('is_certified', e.target.checked)}
                    />
                    Certified
                  </label>
                </div>
                <div className="form-actions">
                  <button className="btn-primary" onClick={handleSave}>Save</button>
                  <button className="btn-secondary" onClick={handleCancel}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="info-grid">
                <div className="info-card">
                  <h3>Contact Information</h3>
                  <p><strong>Email:</strong> {contractor.email}</p>
                  <p><strong>Phone:</strong> {contractor.phone || 'Not provided'}</p>
                  <p><strong>Company:</strong> {contractor.company || 'Not provided'}</p>
                </div>

                <div className="info-card">
                  <h3>Rates</h3>
                  <p><strong>Hourly Rate:</strong> {rate(contractor.hourly_rate)}</p>
                  <p><strong>Billing Rate:</strong> {rate(contractor.billing_rate)}</p>
                </div>

                <div className="info-card">
                  <h3>Configuration</h3>
                  <p><strong>Max Concurrent Tickets:</strong> {contractor.max_concurrent_tickets || 'Not set'}</p>
                  <p><strong>Status:</strong> {contractor.availability_status || 'Available'}</p>
                  <p><strong>Certified:</strong> {contractor.is_certified ? 'Yes' : 'No'}</p>
                </div>

                <div className="info-card">
                  <h3>Activity</h3>
                  <p><strong>Total Hours Worked:</strong> {contractor.total_hours_worked || 0}</p>
                  <p><strong>Last Activity:</strong> {contractor.last_activity_at ? formatDate(contractor.last_activity_at) : 'No activity'}</p>
                  <p><strong>Member Since:</strong> {contractor.created_at ? formatDate(contractor.created_at) : '—'}</p>
                </div>

                {/* Its own row under the cards: as a grid item it was stretched into a
                    card-sized blue tile. */}
                <div className="cp-overview-actions">
                  <button type="button" className="btn btn-primary" onClick={() => setIsEditing(true)}>Edit information</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <div className="performance-section">
            <div className="metric-card">
              <h3>Overall Performance</h3>
              <div className="metrics-grid">
                <div className="metric">
                  <div className="metric-value">
                    {contractor.average_rating ? contractor.average_rating.toFixed(1) : 'N/A'}
                  </div>
                  <div className="metric-label">Average Rating</div>
                  <div className="metric-scale">/5.0</div>
                </div>
                <div className="metric">
                  <div className="metric-value">
                    {contractor.performance_score ? contractor.performance_score.toFixed(0) : 'N/A'}
                  </div>
                  <div className="metric-label">Performance Score</div>
                  <div className="metric-scale">%</div>
                </div>
                <div className="metric">
                  <div className="metric-value">
                    {contractor.total_hours_worked || 0}
                  </div>
                  <div className="metric-label">Total Hours</div>
                </div>
              </div>
            </div>

            {performanceMetrics && (
              <div className="metric-card">
                <h3>Detailed Metrics</h3>
                {/* These read the fields the endpoint actually returns. The
                    previous four (on_time_rate, satisfaction_score,
                    tickets_completed, avg_resolution_time) were never part of
                    any response, so this panel only ever printed N/A. */}
                <div className="metrics-list">
                  <p><strong>Reviews recorded:</strong> {performanceMetrics.reviewCount ?? 0}</p>
                  <p><strong>Performance score:</strong> {
                    performanceMetrics.performanceScore != null
                      ? `${Number(performanceMetrics.performanceScore).toFixed(1)}`
                      : 'Not scored yet'
                  }</p>
                  <p><strong>Hours worked:</strong> {performanceMetrics.hoursWorked ?? 0}</p>
                  <p><strong>Certified:</strong> {performanceMetrics.isCertified ? 'Yes' : 'No'}</p>
                  <p><strong>Last activity:</strong> {
                    performanceMetrics.lastActivityAt
                      ? dateOf(performanceMetrics.lastActivityAt)
                      : 'No recorded activity'
                  }</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Availability Tab */}
        {activeTab === 'availability' && (
          <div className="availability-section">
            <div className="availability-card">
              <h3>Weekly schedule</h3>
              <p className="help-text" style={{ marginBottom: '1rem' }}>
                Toggling a day writes a 09:00&ndash;17:00 block. This was read-only
                before; the endpoint behind it had never been called.
              </p>
              <div className="availability-grid">
                {DAY_NAMES.map((day, index) => {
                  const blocks = availability.filter((b) => b.day_of_week === index);
                  const isAvailable = blocks.some((b) => b.is_available);
                  return (
                    <div key={day} className="day-card">
                      <h4>{day}</h4>
                      <span className={`badge ${isAvailable ? 'badge-resolved' : 'badge-closed'}`}>
                        <i />{isAvailable ? 'Available' : 'Unavailable'}
                      </span>
                      {blocks.map((b) => (
                        <p key={b.id} className="time-block">{b.start_time} &ndash; {b.end_time}</p>
                      ))}
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => handleToggleDay(index, !isAvailable)}
                      >
                        {isAvailable ? 'Mark unavailable' : 'Mark available'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Recent Tickets Tab */}
        {activeTab === 'tickets' && (
          <div className="tickets-section">
            {recentTickets.length > 0 ? (
              <div className="tickets-list">
                {/*
                  * Gordon: "when i click on recent tickets and i get the list
                  * of recent tickets - i cannot click and go on that ticket."
                  *
                  * They were plain divs. A list of tickets on a page about the
                  * person doing them is a list you reach for - there is no
                  * other reason to show it - and the one thing it did was
                  * nothing.
                  *
                  * A real <button> rather than a div with an onClick, like the
                  * dashboard's own tiles and bars: keyboard reachable, and
                  * announced as something that does something. The reference
                  * is shown instead of the raw uuid too - "#a3f8c1d2-..." was
                  * neither clickable nor the number anybody calls it by.
                  */}
                {recentTickets.map(ticket => (
                  /* Compact, three to a row, shaded by stage like the
                     contractor portal's own cards - Gordon: "use a little bit
                     of contrast and shrink the boxes". The global
                     .ticket-header used to wrap the title in a second,
                     padded box of its own. */
                  <button
                    type="button"
                    key={ticket.id}
                    className="ticket-item is-clickable cp-ticket stg-shade"
                    data-stage={stageOf(ticket)}
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                    title={`Open ${ticket.ticket_number || ticket.title}`}
                  >
                    <span className="cp-ticket-top">
                      <span className="ticket-id">{ticket.ticket_number || `#${String(ticket.id).slice(0, 8)}`}</span>
                      <span className="cp-ticket-status">{ticket.status}</span>
                    </span>
                    <span className="cp-ticket-title">{ticket.title}</span>
                    <span className="cp-ticket-date">
                      {ticket.created_at ? formatDate(ticket.created_at) : 'Unknown date'}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="no-data">No recent tickets</p>
            )}
          </div>
        )}

        {tabError && <p className="contractor-error" role="alert">{tabError}</p>}

        {/* SKILLS */}
        {activeTab === 'skills' && (
          <div className="profile-section">
            <form className="inline-form" onSubmit={handleAddSkill}>
              <div className="form-group">
                <label htmlFor="skill-name">Skill</label>
                <input
                  id="skill-name"
                  type="text"
                  value={newSkill.skill_name}
                  onChange={(e) => setNewSkill({ ...newSkill, skill_name: e.target.value })}
                  placeholder="e.g. Network troubleshooting"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="skill-level">Proficiency</label>
                <select
                  id="skill-level"
                  value={newSkill.proficiency_level}
                  onChange={(e) => setNewSkill({ ...newSkill, proficiency_level: e.target.value })}
                >
                  {PROFICIENCY.map((lvl) => (
                    <option key={lvl} value={lvl}>{lvl.charAt(0).toUpperCase() + lvl.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="skill-years">Years</label>
                <input
                  id="skill-years"
                  type="number"
                  min="0"
                  value={newSkill.years_experience}
                  onChange={(e) => setNewSkill({ ...newSkill, years_experience: parseInt(e.target.value, 10) || 0 })}
                />
              </div>
              <button type="submit" className="btn btn-primary">Add skill</button>
            </form>

            {skills.length === 0 ? (
              <p className="no-data">No skills recorded yet</p>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr><th>Skill</th><th>Proficiency</th><th className="num">Years</th><th>Verified</th><th /></tr>
                  </thead>
                  <tbody>
                    {skills.map((skill) => (
                      <tr key={skill.id}>
                        <td><strong>{skill.skill_name}</strong></td>
                        <td>{skill.proficiency_level}</td>
                        <td className="num">{skill.years_experience ?? 0}</td>
                        <td>
                          {skill.is_verified
                            ? <span className="badge badge-resolved"><i />Verified</span>
                            : <button type="button" className="btn btn-plain btn-sm" onClick={() => handleVerifySkill(skill.id)}>Verify</button>}
                        </td>
                        <td>
                          <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDeleteSkill(skill)}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* CERTIFICATIONS */}
        {activeTab === 'certifications' && (
          <div className="profile-section">
            <form className="inline-form" onSubmit={handleAddCertification}>
              <div className="form-group">
                <label htmlFor="cert-name">Certification</label>
                <input
                  id="cert-name"
                  type="text"
                  value={newCert.certification_name}
                  onChange={(e) => setNewCert({ ...newCert, certification_name: e.target.value })}
                  placeholder="e.g. CompTIA Network+"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="cert-org">Issued by</label>
                <input
                  id="cert-org"
                  type="text"
                  value={newCert.issuing_organization}
                  onChange={(e) => setNewCert({ ...newCert, issuing_organization: e.target.value })}
                  placeholder="Organisation"
                />
              </div>
              <div className="form-group">
                <label htmlFor="cert-expiry">Expires</label>
                <input
                  id="cert-expiry"
                  type="date"
                  value={newCert.expiry_date}
                  onChange={(e) => setNewCert({ ...newCert, expiry_date: e.target.value })}
                />
              </div>
              <button type="submit" className="btn btn-primary">Add certification</button>
            </form>

            {certifications.length === 0 ? (
              <p className="no-data">No certifications recorded yet</p>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr><th>Certification</th><th>Issued by</th><th>Expires</th><th>Status</th><th /></tr>
                  </thead>
                  <tbody>
                    {certifications.map((cert) => {
                      const expiry = cert.expiry_date ? new Date(cert.expiry_date) : null;
                      const days = expiry ? Math.ceil((expiry - new Date()) / 86400000) : null;
                      return (
                        <tr key={cert.id}>
                          <td><strong>{cert.certification_name}</strong></td>
                          <td>{cert.issuing_organization || '\u2014'}</td>
                          <td>{expiry ? dateOf(expiry) : 'No expiry'}</td>
                          <td>
                            {days === null && <span className="badge badge-closed"><i />No expiry</span>}
                            {days !== null && days < 0 && <span className="badge badge-open"><i />Expired</span>}
                            {days !== null && days >= 0 && days < 90 && <span className="badge badge-on-hold"><i />{days}d left</span>}
                            {days !== null && days >= 90 && <span className="badge badge-resolved"><i />Valid</span>}
                          </td>
                          <td>
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDeleteCertification(cert)}>Remove</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* WORKLOAD */}
        {activeTab === 'workload' && (
          <div className="profile-section">
            {!workload ? (
              <p className="no-data">No workload data</p>
            ) : (
              <>
                <div className="stat-grid">
                  <div className="stat-tile">
                    <div className="stat-tile-label">Assigned now</div>
                    <div className="stat-tile-value">{workload.currentWorkload ?? workload.current_workload ?? 0}</div>
                  </div>
                  <div className="stat-tile">
                    <div className="stat-tile-label">Capacity</div>
                    <div className="stat-tile-value">{workload.maxConcurrentTickets ?? workload.max_concurrent_tickets ?? 5}</div>
                  </div>
                  <div className="stat-tile">
                    <div className="stat-tile-label">Utilisation</div>
                    <div className="stat-tile-value">{Math.round(Number(workload.utilizationRate ?? 0))}%</div>
                  </div>
                </div>
                <div className="inline-form">
                  <div className="form-group">
                    <label htmlFor="max-tickets">Maximum concurrent tickets</label>
                    <input
                      id="max-tickets"
                      type="number"
                      min="1"
                      max="50"
                      defaultValue={workload.maxConcurrentTickets ?? workload.max_concurrent_tickets ?? 5}
                      onBlur={(e) => handleSetMaxTickets(parseInt(e.target.value, 10) || 1)}
                    />
                    <span className="help-text">Saved when you click away from the field.</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* CONTRACTS (120): every contract with them, and adding one. */}
        {activeTab === 'contracts' && contractsOn && <ContractorContracts contractorId={id} />}

        {/* BILLING */}
        {activeTab === 'billing' && (
          <div className="profile-section">
            <div className="stat-grid">
              <div className="stat-tile">
                <div className="stat-tile-label">Hourly rate</div>
                <div className="stat-tile-value">
                  {contractor.hourly_rate != null ? `\u20ac${Number(contractor.hourly_rate).toFixed(2)}` : '\u2014'}
                </div>
              </div>
              <div className="stat-tile">
                <div className="stat-tile-label">Billing rate</div>
                <div className="stat-tile-value">
                  {contractor.billing_rate != null ? `\u20ac${Number(contractor.billing_rate).toFixed(2)}` : '\u2014'}
                </div>
              </div>
            </div>

            <form className="inline-form" onSubmit={lookupInvoice}>
              <div className="form-group">
                <label htmlFor="invoice-lookup">Look up an invoice number</label>
                <input
                  id="invoice-lookup" type="search" value={invoiceQuery}
                  placeholder="e.g. INV-2026-0104"
                  onChange={(e) => setInvoiceQuery(e.target.value)}
                />
                <span className="help-text">
                  Shows every ticket the contractor billed against that reference.
                </span>
              </div>
              <button type="submit" className="btn btn-primary">Look up</button>
            </form>

            {invoiceResult && (
              <div className="card">
                <div className="card-header">
                  <h3>{invoiceResult.invoice_number}</h3>
                  <span className="muted">
                    {invoiceResult.ticket_count} ticket{invoiceResult.ticket_count === 1 ? '' : 's'}
                    {' \u00b7 '}
                    <strong>{`\u20ac${Number(invoiceResult.total_amount || 0).toFixed(2)}`}</strong>
                  </span>
                </div>
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr><th>Ticket</th><th className="num">Hours</th><th className="num">Rate</th><th className="num">Amount</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {invoiceResult.entries.map((e) => (
                        <tr key={e.id}>
                          <td>
                            <div className="nm"><strong>{e.ticket_number || '\u2014'}</strong></div>
                            <div className="sub">{e.title || ''}</div>
                          </td>
                          <td className="num">{e.hours_worked ?? 0}</td>
                          <td className="num">{`\u20ac${Number(e.hourly_rate || 0).toFixed(2)}`}</td>
                          <td className="num">{`\u20ac${Number(e.total_amount || 0).toFixed(2)}`}</td>
                          <td><span className="badge badge-resolved"><i />{e.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {billing.length === 0 ? (
              <p className="no-data">No billing entries recorded</p>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr><th>Period</th><th className="num">Hours</th><th className="num">Rate</th><th className="num">Amount</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {billing.map((entry) => (
                      <tr key={entry.id}>
                        <td>{entry.billing_period_start ? dateOf(entry.billing_period_start) : '\u2014'}</td>
                        <td className="num">{entry.hours_worked ?? 0}</td>
                        <td className="num">{entry.hourly_rate != null ? `\u20ac${Number(entry.hourly_rate).toFixed(2)}` : '\u2014'}</td>
                        <td className="num">{entry.total_amount != null ? `\u20ac${Number(entry.total_amount).toFixed(2)}` : '\u2014'}</td>
                        <td>
                          <span className={`badge ${entry.status === 'paid' ? 'badge-resolved' : entry.status === 'disputed' ? 'badge-open' : 'badge-on-hold'}`}>
                            <i />{entry.status || 'pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      {confirmDialog}
    </div>
  );
};

export default ContractorProfile;
